import { SLACK_COLORS, SLACK_FONTS, SLACK_SPACING } from '@/lib/slack-styles';
import { SlackBlockRenderer } from './slack-block-renderer';
import { MrkdwnRenderer } from './mrkdwn-renderer';
import type { SlackBlock } from '@/lib/slack-styles';
import { Loader2 } from 'lucide-react';

interface SlackPreviewProps {
  blocks: SlackBlock[];
  botName?: string;
  botAvatar?: string;
  timestamp?: Date;
  isLoading?: boolean;
  formattedText?: string;
}

/**
 * Pixel-perfect Slack message preview
 */
// OREO bot avatar URL from Slack
const OREO_AVATAR = 'https://avatars.slack-edge.com/2025-11-19/9945428979701_77e4a95a1c49c4962bcc_512.png';

export function SlackPreview({
  blocks,
  botName = 'OREO',
  botAvatar = OREO_AVATAR,
  timestamp = new Date(),
  isLoading = false,
  formattedText,
}: SlackPreviewProps) {
  const formattedTime = formatSlackTime(timestamp);

  return (
    <div
      style={{
        fontFamily: SLACK_FONTS.family,
        fontSize: SLACK_FONTS.sizeBase,
        lineHeight: SLACK_FONTS.lineHeight,
        backgroundColor: SLACK_COLORS.background,
        color: SLACK_COLORS.text,
        padding: '12px 16px',
        minHeight: '100px',
      }}
    >
      {/* Message header: avatar, bot name, timestamp */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: SLACK_SPACING.avatarGap,
          marginBottom: '4px',
        }}
      >
        {/* Bot avatar */}
        <div
          style={{
            width: SLACK_SPACING.avatarSize,
            height: SLACK_SPACING.avatarSize,
            borderRadius: '4px',
            backgroundColor: botAvatar ? 'transparent' : SLACK_COLORS.avatarBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {botAvatar ? (
            <img
              src={botAvatar}
              alt={botName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <span
              style={{
                color: SLACK_COLORS.avatarText,
                fontSize: '16px',
                fontWeight: 700,
              }}
            >
              {botName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Bot name and timestamp */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '8px',
              marginBottom: '2px',
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: SLACK_FONTS.sizeBase,
                color: SLACK_COLORS.text,
              }}
            >
              {botName}
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: SLACK_COLORS.textSecondary,
                  backgroundColor: SLACK_COLORS.hoverBg,
                  padding: '1px 4px',
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                APP
              </span>
            </span>
            <span
              style={{
                fontSize: SLACK_FONTS.sizeTiny,
                color: SLACK_COLORS.textMuted,
              }}
            >
              {formattedTime}
            </span>
          </div>

          {/* Message content */}
          <div
            style={{
              paddingTop: '2px',
            }}
          >
            {isLoading ? (
              <LoadingSkeleton />
            ) : blocks.length > 0 ? (
              <div>
                {blocks.map((block, index) => (
                  <SlackBlockRenderer key={index} block={block} />
                ))}
              </div>
            ) : formattedText ? (
              <MrkdwnRenderer text={formattedText} />
            ) : (
              <EmptyPreview />
            )}
          </div>
        </div>
      </div>

      {/* Feedback buttons (non-interactive preview) */}
      {!isLoading && blocks.length > 0 && (
        <div
          style={{
            marginTop: '12px',
            marginLeft: '44px', // Align with message content
            display: 'flex',
            gap: '8px',
          }}
        >
          <PreviewButton emoji="👍" />
          <PreviewButton emoji="👎" />
        </div>
      )}
    </div>
  );
}

/**
 * Loading skeleton for preview
 */
function LoadingSkeleton() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Loader2
          className="animate-spin"
          style={{ width: '16px', height: '16px', color: SLACK_COLORS.textMuted }}
        />
        <span style={{ color: SLACK_COLORS.textSecondary, fontSize: '13px' }}>
          Formatting with AI...
        </span>
      </div>
      <div
        style={{
          height: '16px',
          backgroundColor: SLACK_COLORS.hoverBg,
          borderRadius: '4px',
          width: '80%',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div
        style={{
          height: '16px',
          backgroundColor: SLACK_COLORS.hoverBg,
          borderRadius: '4px',
          width: '60%',
          animation: 'pulse 1.5s ease-in-out infinite',
          animationDelay: '0.15s',
        }}
      />
      <div
        style={{
          height: '16px',
          backgroundColor: SLACK_COLORS.hoverBg,
          borderRadius: '4px',
          width: '70%',
          animation: 'pulse 1.5s ease-in-out infinite',
          animationDelay: '0.3s',
        }}
      />
    </div>
  );
}

/**
 * Empty preview placeholder
 */
function EmptyPreview() {
  return (
    <div
      style={{
        color: SLACK_COLORS.textMuted,
        fontStyle: 'italic',
        padding: '16px 0',
      }}
    >
      Start typing to see a preview of your formatted response...
    </div>
  );
}

/**
 * Preview-only feedback button
 */
function PreviewButton({ emoji }: { emoji: string }) {
  return (
    <button
      disabled
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
        fontSize: '13px',
        color: SLACK_COLORS.textSecondary,
        backgroundColor: 'transparent',
        border: `1px solid ${SLACK_COLORS.border}`,
        borderRadius: '4px',
        cursor: 'default',
        opacity: 0.6,
      }}
    >
      {emoji}
    </button>
  );
}

/**
 * Format timestamp like Slack does
 */
function formatSlackTime(date: Date): string {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');

  if (isToday) {
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  }

  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day} at ${formattedHours}:${formattedMinutes} ${ampm}`;
}

export default SlackPreview;
